import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'react-hot-toast';
import { AlertCircle } from 'lucide-react';
import { User } from '@supabase/supabase-js';

interface ProfileSetupProps {
  user: User;
  onComplete: () => void;
  onCancel: () => void;
}

export default function ProfileSetup({ user, onComplete, onCancel }: ProfileSetupProps) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({
    username: '',
    first_name: '',
    last_name: '',
    display_name: '',
    bio: '',
    date_of_birth: '',
    city: '',
    country: '',
    is_public: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateUsername = async (username: string): Promise<string> => {
    if (!username) return 'Username is required';
    if (username.length < 3) return 'Username must be at least 3 characters';
    if (username.length > 30) return 'Username must be less than 30 characters';
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      return 'Username can only contain letters, numbers, and underscores';
    }

    try {
      // Check if username is taken
      const { count } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('username', username);

      if (count && count > 0) return 'Username is already taken';
      return '';
    } catch (error) {
      console.error('Error checking username:', error);
      return 'Error checking username availability';
    }
  };

  const validateForm = async () => {
    const newErrors: Record<string, string> = {};

    // Step 1 validation
    if (step === 1) {
      if (!profile.username) {
        newErrors.username = 'Username is required';
      } else {
        const usernameError = await validateUsername(profile.username);
        if (usernameError) newErrors.username = usernameError;
      }

      if (!profile.first_name) newErrors.first_name = 'First name is required';
      if (!profile.last_name) newErrors.last_name = 'Last name is required';
    }

    // Step 2 validation
    if (step === 2) {
      if (!profile.date_of_birth) {
        newErrors.date_of_birth = 'Date of birth is required';
      } else {
        const birthDate = new Date(profile.date_of_birth);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }

        if (age < 12) {
          newErrors.date_of_birth = 'You must be at least 12 years old';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (step === 1) {
      const isValid = await validateForm();
      if (!isValid) return;
      setStep(2);
      return;
    }

    const isValid = await validateForm();
    if (!isValid) return;

    setLoading(true);
    try {
      // Update profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          username: profile.username,
          first_name: profile.first_name,
          last_name: profile.last_name,
          display_name: profile.display_name || `${profile.first_name} ${profile.last_name}`,
          bio: profile.bio,
          date_of_birth: profile.date_of_birth,
          city: profile.city,
          country: profile.country,
          is_public: profile.is_public
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      toast.success('Profile setup complete!');
      onComplete();
    } catch (error: any) {
      console.error('Error setting up profile:', error);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 py-12">
      <div className="max-w-md mx-auto bg-white rounded-xl shadow-sm p-8">
        <h1 className="text-2xl font-bold text-center mb-8">Complete Your Profile</h1>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {step === 1 && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username *
                </label>
                <div className="relative">
                  <Input
                    value={profile.username}
                    onChange={(e) => {
                      setProfile({ ...profile, username: e.target.value.toLowerCase() });
                      setErrors({ ...errors, username: '' });
                    }}
                    placeholder="Choose a username"
                    error={!!errors.username}
                  />
                  {errors.username && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500">
                      <AlertCircle className="h-5 w-5" />
                    </div>
                  )}
                </div>
                {errors.username && (
                  <p className="mt-1 text-sm text-red-500">{errors.username}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  This will be your unique identifier in the community
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <Input
                    value={profile.first_name}
                    onChange={(e) => {
                      setProfile({ ...profile, first_name: e.target.value });
                      setErrors({ ...errors, first_name: '' });
                    }}
                    error={!!errors.first_name}
                  />
                  {errors.first_name && (
                    <p className="mt-1 text-sm text-red-500">{errors.first_name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <Input
                    value={profile.last_name}
                    onChange={(e) => {
                      setProfile({ ...profile, last_name: e.target.value });
                      setErrors({ ...errors, last_name: '' });
                    }}
                    error={!!errors.last_name}
                  />
                  {errors.last_name && (
                    <p className="mt-1 text-sm text-red-500">{errors.last_name}</p>
                  )}
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
              >
                Next
              </Button>
            </>
          )}

          {step === 2 && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name
                </label>
                <Input
                  value={profile.display_name}
                  onChange={(e) => setProfile({ ...profile, display_name: e.target.value })}
                  placeholder={`${profile.first_name} ${profile.last_name}`}
                />
                <p className="mt-1 text-sm text-gray-500">
                  Leave blank to use your full name
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bio
                </label>
                <textarea
                  value={profile.bio}
                  onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  rows={3}
                  placeholder="Tell us about yourself..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth *
                </label>
                <div className="relative">
                  <Input
                    type="date"
                    value={profile.date_of_birth}
                    onChange={(e) => {
                      setProfile({ ...profile, date_of_birth: e.target.value });
                      setErrors({ ...errors, date_of_birth: '' });
                    }}
                    error={!!errors.date_of_birth}
                  />
                  {errors.date_of_birth && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500">
                      <AlertCircle className="h-5 w-5" />
                    </div>
                  )}
                </div>
                {errors.date_of_birth && (
                  <p className="mt-1 text-sm text-red-500">{errors.date_of_birth}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <Input
                    value={profile.city}
                    onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <Input
                    value={profile.country}
                    onChange={(e) => setProfile({ ...profile, country: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_public"
                  checked={profile.is_public}
                  onChange={(e) => setProfile({ ...profile, is_public: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="is_public" className="ml-2 block text-sm text-gray-700">
                  Make profile public
                </label>
              </div>

              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="ghost"
                  className="flex-1"
                  onClick={() => setStep(1)}
                  disabled={loading}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={loading}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}